import {
  auditStamp,
  dailyTotalOf,
  dailyTotalSentence,
  tierHistoryOf,
  toolUsageOf,
  type McpTenantDetail,
  type McpTierHistory,
  type McpUsage,
} from "../../../api/mcp";
import { BlockControl } from "../../../components/mcp/BlockControl";
import { TierControl } from "../../../components/mcp/TierControl";
import { Reason } from "../../../components/write/ReasonText";
import { ResourceView, statOf } from "../../../components/states";
import { Badge, Card, Empty, Field, Fields, Stat, StatRow, Table, Td, Th } from "../../../components/ui";
import type { AppConfig } from "../../../config";
import { Button } from "../../../f7";
import { useResource } from "../../../hooks/useResource";
import { ProductSection } from "../../../products/PerProduct";

export function McpRecord({
  config,
  reference,
  detail,
  usage,
  history,
  reloadDetail,
}: {
  config: AppConfig;
  reference: string;
  detail: McpTenantDetail;
  usage: ReturnType<typeof useResource<McpUsage>>;
  history: ReturnType<typeof useResource<McpTierHistory>>;
  reloadDetail: () => void;
}) {
  const enc = encodeURIComponent(reference);
  const d = detail;
  const u = usage.state.kind === "ready" ? usage.state.data : null;
  const dailyTile = statOf(usage.state, (row) => {
    const spent = dailyTotalOf(row)?.spent;
    return typeof spent === "number" ? spent : null;
  });
  const dailyMode = u ? dailyTotalOf(u)?.mode : undefined;

  // `blocklist: {blocked, matched}` is GONE from this response — MCP
  // flattened it to `blocked` + `blocked_since`/`blocked_by`/
  // `blocked_reason` (3e43b98). Same three facts the old `matched.
  // merchant_wide` row carried, under their new names.
  const wide = d.blocked
    ? { reason: d.blocked_reason, added_by: d.blocked_by, added_at: d.blocked_since }
    : null;

  return (
    <ProductSection id="mcp" note="Tier, access, usage and caps">
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Plan tier">
        <Fields>
          <Field label="MCP">
            <Badge tone={d.tier ? "neutral" : "amber"}>{d.effective_tier}</Badge>
            {/* `tier: null` means nothing is stored. The server
                treats them as free, but reporting the stored value
                honestly is the point — a GET never creates the row. */}
            {d.tier_is_implicit ? (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Nothing is stored for this merchant. The server treats them as{" "}
                <span className="ref">{d.effective_tier}</span>; reading this page
                does not create the row.
              </p>
            ) : (
              d.tier_source && (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Stored as <span className="ref">{d.tier}</span>, set by{" "}
                  <span className="ref">{d.tier_source.replace(/^admin:/, "")}</span>
                  {d.tier_updated_at &&
                    ` on ${auditStamp(d.tier_updated_at, { dateOnly: true })}`}
                </p>
              )
            )}
          </Field>
          {/* NO Labeeb row here. A block titled MCP that reports
              Labeeb's state is the same mistake as a column reading
              "Labeeb not wired" on every row: the reader came for
              one product and got a line about the other.

              The tier SPLIT is still visible — Labeeb's own block
              renders both values, which is one place rather than
              two saying it. */}
        </Fields>

        <div className="mt-4 flex flex-col gap-3">
          <TierControl
            config={config}
            reference={d.business_reference}
            name={d.business_name}
            currentTier={d.effective_tier}
            onDone={() => {
              reloadDetail();
              history.reload();
            }}
          />
        </div>
      </Card>

      <Card title="Access">
        <Fields>
          <Field label="MCP">
            {d.blocked ? (
              <Badge tone="red">Blocked</Badge>
            ) : (
              <Badge tone="green">Active</Badge>
            )}
            {wide && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {wide.reason && (
                  <>
                    Reason: <Reason text={wide.reason} />.{" "}
                  </>
                )}
                {wide.added_by && (
                  <>
                    Blocked by <span className="ref">{wide.added_by}</span>
                  </>
                )}
                {wide.added_at && ` on ${auditStamp(wide.added_at, { dateOnly: true })}`}
              </p>
            )}
          </Field>

        </Fields>

        {/* The caveat belongs to THIS control, not to a row about
            the other product: blocking here stops MCP tool calls,
            and says nothing about the assistant. */}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Blocking stops MCP tool calls only. A merchant blocked here can still
          be talking to the assistant.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <BlockControl
            config={config}
            scope={{ kind: "merchant", reference: d.business_reference }}
            blocked={d.blocked}
            onDone={reloadDetail}
          />
          <Button plain href={`/tenants/${enc}/users`}>
            Blocked users
          </Button>
        </div>
      </Card>
    </div>

    {/* The panel's action belongs in its HEADER, which is the slot
        Card gives it and the slot the design uses. As a `justify-end`
        row above the body it pushed the figures down by its own
        height and lined up with nothing. */}
    <Card
      title="Today"
      aside={
        <Button plain href={`/tenants/${enc}/usage`}>
          All usage
        </Button>
      }
    >
      <StatRow>
        <Stat
          label="Credits today"
          to={`/tenants/${enc}/usage`}
          {...statOf(usage.state, (u) => u.total_credits)}
        />
        <Stat
          label="Tools used"
          to={`/tenants/${enc}/usage`}
          {...statOf(usage.state, (u) => Object.keys(toolUsageOf(u)).length)}
        />
        <Stat
          label="UTC day"
          hint={u?.day_boundary}
          {...statOf(usage.state, (u) => u.day)}
        />
        <Stat label="Daily total" value={dailyTile.value} hint={dailyMode ?? dailyTile.hint} />
      </StatRow>
      {u && dailyTotalSentence(u) && (
        <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">{dailyTotalSentence(u)}</p>
      )}
    </Card>

    {/* Caps are per tier, never per merchant: their one home is
        GET /admin/quota/{tier}, the page this card's own "Tier
        caps" button sends someone to. */}
    <Card
      title="Caps"
      note={
        <>
          Caps for tier <span className="ref">{d.effective_tier}</span> live on the
          tier's own page now
          {d.tier_is_implicit && " (implicit — no tier is stored)"}.
        </>
      }

    >
      <Button href={`/policy/caps/${encodeURIComponent(d.effective_tier)}`}>
        Open {d.effective_tier} caps
      </Button>
    </Card>

    <Card
      title="Recent tier changes"
      aside={
        <Button plain href={`/tenants/${enc}/history`}>
          Full history
        </Button>
      }
    >
      <ResourceView state={history.state} what="tier history" onRetry={history.reload}>
      {(h) => tierHistoryOf(h).length > 0 ? (
        <Table head={<tr><Th>Tier</Th><Th>Set by</Th><Th>When</Th></tr>}>
          {tierHistoryOf(h).slice(0, 3).map((e, i) => (
            <tr key={i}>
              <Td>
                <Badge>{e.tier}</Badge>
              </Td>
              <Td>
                {e.source === null ? (
                  <span className="text-xs text-slate-500 dark:text-slate-400">unknown</span>
                ) : e.source === "signup" ? (
                  <span className="text-xs text-slate-500 dark:text-slate-400">automatic — first use</span>
                ) : (
                  <span className="ref text-xs">{e.source.replace(/^admin:/, "")}</span>
                )}
              </Td>
              <Td>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {e.created_at ? auditStamp(e.created_at, { dateOnly: true }) : "—"}
                </span>
              </Td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty>No recorded changes.</Empty>
      )}
      </ResourceView>
    </Card>
    </ProductSection>
  );
}
